import { defineComponent, onBeforeUnmount, onMounted, ref, useContext, useCssModule, useRoute, useRouter, watch, } from '@nuxtjs/composition-api';
import { useTask } from 'vue-concurrency';
import { DROPDOWN_RUBRICS_ITEMS } from './';
import { DROPDOWN_YEAR_ITEMS, useBrandvoices, useSearch } from '~/composables';
import { useDuck } from '~/services';
import { getPubSearch } from '~/utils';
import { ARCHIVE_OTHER, ARCHIVE_RUBRICS } from '~/pages/archive/data';
export default defineComponent({
    name: 'ArchiveSearch',
    props: {
        title: {
            default: 'Поиск',
            type: String,
        },
        entries: {
            default: () => [],
            type: Array,
        },
    },
    setup(props) {
        /**
         * Composables
         */
        const brandvoices = useBrandvoices();
        const context = useContext();
        const css = useCssModule();
        const duck = useDuck();
        const search = useSearch();
        const route = useRoute();
        const router = useRouter();
        /**
         * DayJS
         */
        const dayjs = context.$dayjs;
        const DROPDOWN_BRANDVOICE_ITEMS = [
            // {
            //   title: 'Все блоги',
            //   value: '/archive/brandvoice',
            // },
            ...brandvoices.entries.reduce((entries, { name: title, url_alias: value }) => {
                if (!value) {
                    return entries;
                }
                title = title || value;
                value = `/archive/${value}`;
                entries.push({
                    title,
                    value,
                });
                return entries;
            }, []),
        ];
        const DROPDOWN_ARCHIVE_ITEMS = [
            {
                title: 'Весь архив',
                value: '/archive',
            },
            ...DROPDOWN_RUBRICS_ITEMS,
            ...DROPDOWN_BRANDVOICE_ITEMS,
            ...ARCHIVE_OTHER.map(({ title, to: value }) => ({
                title,
                value,
            })),
        ];
        const categoryDropdownValue = ref(route.value.path);
        const hasErrors = ref(false);
        const isDatePickerOpened = ref(false);
        /**
         * Search task
         */
        const searchTask = useTask(function* () {
            try {
                search.set('hasSearched', true);
                search.set('articles', []);
                search.set('error', '');
                search.set('isLoading', true);
                search.set('notFound', false);
                const { data: articles, headers } = yield getPubSearch({
                    brandvoices,
                    duck,
                    route: route.value,
                }, {
                    'list[limit]': search.limit,
                    'list[offset]': 0,
                    'node[title]': search.query,
                    ...(search.dateRange.length && {
                        'node[sort]': search.backendSortType,
                        'node[start]': String(dayjs(search.dateRange[0], search.dateFormat).unix()),
                        'node[end]': String(dayjs(search.dateRange[1], search.dateFormat)
                            .startOf('day')
                            .add(23, 'hour')
                            .add(59, 'minute')
                            .add(59, 'second')
                            .unix()),
                    }),
                });
                if (!articles.length) {
                    search.set('notFound', true);
                    search.set('total', 0);
                    return;
                }
                search.set('articles', articles);
                const total = Number(headers?.get('x-total-count')) || 0;
                search.set('total', total);
            }
            catch (error) {
                console.error('[Search]', error);
                search.set('error', error);
            }
            finally {
                search.set('isLoading', false);
                search.set('completedQuery', search.query);
            }
        });
        function handleBreadcrumbsClick({ to }) {
            const { to: lastEntryTo = '' } = props.entries.at(-1);
            if (lastEntryTo === '/archive' && search.hasSearched) {
                return search.clearAll();
            }
            if (to === lastEntryTo) {
                search.clearAll();
                searchTask.perform();
            }
        }
        function clearInput() {
            search.clear();
            searchTask.perform();
        }
        function handleCategoryDropdown(value) {
            categoryDropdownValue.value = value;
            router.push(value);
        }
        function handleInput() {
            hasErrors.value = false;
            if (search.query === '') {
                clearInput();
            }
        }
        function handleDateDropdown(value) {
            const entry = DROPDOWN_YEAR_ITEMS?.find((item) => item.value === value);
            search.set('dateRangeEntry', entry);
            if (value === 'range') {
                search.set('dateRange', []);
                isDatePickerOpened.value = !isDatePickerOpened.value;
                return;
            }
            if (value === 'all') {
                search.set('dateRange', []);
            }
            else {
                const today = dayjs().startOf('day').format(search.dateFormat);
                const timeAgo = dayjs().startOf('day').subtract(1, value).add(1, 'day').format(search.dateFormat);
                search.set('dateRange', [timeAgo, today]);
            }
            searchTask.perform();
        }
        function handleDatePickerCancel() {
            search.set('dateRange', []);
            search.set('dateRangeEntry', DROPDOWN_YEAR_ITEMS[0]);
            isDatePickerOpened.value = false;
            searchTask.perform();
        }
        function handleDatePickerConfirm() {
            isDatePickerOpened.value = false;
            searchTask.perform();
        }
        function handleSubmit() {
            if (categoryDropdownValue.value === route.value.path) {
                return searchTask.perform();
            }
            router.push(categoryDropdownValue.value);
        }
        function shouldShowCategoryDropdown(category) {
            const { path } = route.value;
            const items = category === 'brandvoice' ? [...brandvoices.archiveEntries] : Object.values(ARCHIVE_RUBRICS);
            for (const { to } of items) {
                if (path.includes(to) || path.includes(category)) {
                    return true;
                }
            }
            return false;
        }
        onBeforeUnmount(() => {
            search.clearAll();
        });
        onMounted(() => {
            watch(route, ({ path }) => {
                categoryDropdownValue.value = path;
                hasErrors.value = false;
            });
        });
        return {
            DROPDOWN_ARCHIVE_ITEMS,
            DROPDOWN_BRANDVOICE_ITEMS,
            DROPDOWN_RUBRICS_ITEMS,
            DROPDOWN_YEAR_ITEMS,
            categoryDropdownValue,
            css,
            clearInput,
            handleBreadcrumbsClick,
            handleCategoryDropdown,
            handleDateDropdown,
            handleDatePickerCancel,
            handleDatePickerConfirm,
            handleInput,
            handleSubmit,
            hasErrors,
            isDatePickerOpened,
            search,
            searchTask,
            shouldShowCategoryDropdown,
        };
    },
});
